import type { OrgMemberRole } from "@solomon-apps/shared";
import {
    Combobox,
    ComboboxCollection,
    ComboboxContent,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    InputGroupAddon,
    InputGroupText
} from "@solomon-labs/design/components/ui";
import React, { useEffect, useState } from "react";

import { ROLE_ORDER } from "../teamData";

/**
 * The Role choice as a label-inside light-bar field: the DS Combobox (the
 * same `ComboboxInput` recipe as the Rewards Destination picker), "Role" in
 * the inline-start addon, Owner and Member in the list. Single-select: one
 * role per member, which is what the API stores. Typing filters the list;
 * the field only ever settles on a role, so text that matches nothing goes
 * back to the held role (Member for a new invite) when the list closes.
 * The member page's Role inline edit and the Add Team Member page share it,
 * so a role is chosen the same way in both.
 *
 * `actions` sit at the inline end (the inline edit's save and cancel).
 */
export function RoleChoiceField({
    value,
    onChange,
    disabled = false,
    autoFocus = false,
    actions
}: {
    value: OrgMemberRole;
    onChange: (next: OrgMemberRole) => void;
    disabled?: boolean;
    /** Focus the field on mount (the inline edit opening). */
    autoFocus?: boolean;
    actions?: React.ReactNode;
}): React.ReactElement {
    // Controlled text: it follows the held role, and returns to it whenever
    // the list closes, so a typed non-role never sticks.
    const [query, setQuery] = useState<string>(value);
    useEffect(() => setQuery(value), [value]);

    return (
        <Combobox<OrgMemberRole>
            items={ROLE_ORDER}
            value={value}
            onValueChange={(next: OrgMemberRole | null) => {
                // A clear or an unmatched entry keeps the held role.
                if (next && !disabled) onChange(next);
            }}
            inputValue={query}
            onInputValueChange={(next: string) => setQuery(next)}
            onOpenChange={(open: boolean) => {
                if (!open) setQuery(value);
            }}
            // Typing highlights the first match, so Enter picks it.
            autoHighlight
            // An untouched field lists every role, not just the held one.
            filter={(role: OrgMemberRole, search: string) =>
                search === value ||
                role.toLowerCase().includes(search.trim().toLowerCase())
            }
            disabled={disabled}
        >
            <ComboboxInput
                aria-label="Role"
                className="w-full"
                showClear={false}
                disabled={disabled}
                autoFocus={autoFocus}
            >
                <InputGroupAddon align="inline-start">
                    <InputGroupText className="w-24">Role</InputGroupText>
                </InputGroupAddon>
                {actions && (
                    <InputGroupAddon align="inline-end" className="gap-1">
                        {actions}
                    </InputGroupAddon>
                )}
            </ComboboxInput>
            <ComboboxContent>
                <ComboboxList>
                    <ComboboxCollection>
                        {(role: OrgMemberRole) => (
                            <ComboboxItem key={role} value={role}>
                                {role}
                            </ComboboxItem>
                        )}
                    </ComboboxCollection>
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    );
}
